(() => {
  const KEY = 'dimark-wishlist';
  const config = window.dimarkWishlistConfig || {};
  const META_NS = config.metafieldNamespace || 'custom';
  const META_KEY = config.metafieldKey || 'buy_later_wish_list';
  const META_TYPE = config.metafieldType || 'list.product_reference';

  function normalize(raw) {
    if (!raw) return [];
    if (typeof raw === 'string') {
      try {
        return normalize(JSON.parse(raw));
      } catch (error) {
        return [];
      }
    }
    if (!Array.isArray(raw)) return [];
    return raw.map((item) => {
      if (item == null) return null;
      if (typeof item === 'string' || typeof item === 'number') {
        const id = String(item).replace(/\D/g, '');
        return id ? { productId: id, variantId: id } : null;
      }
      const productId = String(item.productId || item.product_id || item.id || '').replace(/\D/g, '');
      const variantId = String(item.variantId || item.variant_id || productId);
      if (!productId && !variantId) return null;
      const savedCents = Number(item.priceCents || item.price_cents);
      return {
        productId: productId || variantId,
        variantId,
        handle: item.handle || '',
        title: item.title || '',
        url: item.url || '',
        price: typeof item.price === 'string' ? item.price.replace(/<[^>]*>/g, '').trim() : '',
        priceCents: Number.isFinite(savedCents) && savedCents > 0 ? savedCents : null,
        type: item.type || item.product_type || '',
        image: item.image || item.featured_image || ''
      };
    }).filter(Boolean);
  }

  function readLocal() {
    try {
      const raw = localStorage.getItem(KEY);
      return normalize(raw ? JSON.parse(raw) : []);
    } catch (error) {
      return [];
    }
  }

  function mergeLists(primary, secondary) {
    const seen = {};
    const out = [];
    primary.concat(secondary).forEach((item) => {
      const id = idOf(item);
      if (!id || seen[id]) return;
      seen[id] = true;
      out.push(item);
    });
    return out;
  }

  function idOf(item) {
    return String(item.variantId || item.productId || '');
  }

  let list = mergeLists(normalize(config.seed), readLocal());

  function persistLocal(next) {
    localStorage.setItem(KEY, JSON.stringify(next));
  }

  function productGids(next) {
    const seen = {};
    const gids = [];
    next.forEach((item) => {
      const id = String(item.productId || item.variantId || '').replace(/\D/g, '');
      if (!id || seen[id]) return;
      seen[id] = true;
      gids.push('gid://shopify/Product/' + id);
    });
    return gids;
  }

  function persistMetafield(next) {
    if (!config.customerId) return;

    const ownerId = 'gid://shopify/Customer/' + config.customerId;
    const gids = productGids(next);
    let payload;

    if (!gids.length) {
      payload = {
        query: `mutation MetafieldsDelete($metafields: [MetafieldIdentifierInput!]!) {
          metafieldsDelete(metafields: $metafields) {
            userErrors { field message }
          }
        }`,
        variables: {
          metafields: [{
            ownerId,
            namespace: META_NS,
            key: META_KEY
          }]
        }
      };
    } else {
      payload = {
        query: `mutation MetafieldsSet($metafields: [MetafieldsSetInput!]!) {
          metafieldsSet(metafields: $metafields) {
            userErrors { field message }
          }
        }`,
        variables: {
          metafields: [{
            ownerId,
            namespace: META_NS,
            key: META_KEY,
            type: META_TYPE,
            value: JSON.stringify(gids)
          }]
        }
      };
    }

    const endpoint = 'https://dimarklimited.myshopify.com/account/customer/api/2026-07/graphql';

    fetch(endpoint, {
      method: 'POST',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json'
      },
      body: JSON.stringify(payload)
    }).catch(() => {});
  }

  function write(next) {
    list = next;
    persistLocal(next);
    persistMetafield(next);
    document.dispatchEvent(new CustomEvent('on:wishlist:change', { detail: { items: next } }));
  }

  function read() {
    return list.slice();
  }

  function has(variantId) {
    const id = String(variantId);
    return list.some((item) => idOf(item) === id);
  }

  function toggle(data) {
    const entry = normalize([data])[0];
    if (!entry) return false;
    const next = list.slice();
    const id = idOf(entry);
    const index = next.findIndex((item) => idOf(item) === id);
    if (index >= 0) next.splice(index, 1);
    else next.unshift(entry);
    write(next);
    return index < 0;
  }

  function remove(variantId) {
    write(list.filter((item) => idOf(item) !== String(variantId)));
  }

  function removeMany(variantIds, animate) {
    const ids = new Set(
      (Array.isArray(variantIds) ? variantIds : [variantIds])
        .map(String)
        .filter(Boolean)
    );
    if (!ids.size) return;

    const doRemove = () => {
      write(list.filter((item) => !ids.has(idOf(item))));
    };

    if (animate && !window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      ids.forEach((id) => {
        const tile = document.querySelector(`[data-wish-id="${CSS.escape(String(id))}"]`);
        if (tile) tile.classList.add('is-leaving');
      });
      setTimeout(doRemove, 220);
    } else {
      doRemove();
    }
  }

  function syncButton(btn) {
    const pressed = has(btn.dataset.variantId);
    btn.setAttribute('aria-pressed', pressed ? 'true' : 'false');
    const label = pressed ? btn.dataset.unsaveLabel : btn.dataset.saveLabel;
    if (label) btn.setAttribute('aria-label', label);
    const textEl = btn.querySelector('.ap-wish-btn__label');
    if (textEl) {
      textEl.textContent = pressed
        ? (btn.dataset.savedText || 'In wishlist')
        : (btn.dataset.saveText || 'Add to wishlist');
    }
  }

  function syncButtons(root) {
    (root || document).querySelectorAll('[data-wishlist-toggle]').forEach(syncButton);
  }

  function syncCounts() {
    const count = list.length;
    document.querySelectorAll('[data-wishlist-count]').forEach((el) => {
      if (count > 0) el.removeAttribute('hidden');
      else el.setAttribute('hidden', '');
    });
    document.querySelectorAll('[data-wishlist-count-number]').forEach((el) => {
      el.textContent = String(count);
    });
    document.querySelectorAll('account-profile [data-tab-meta="wishlist"]').forEach((el) => {
      el.textContent = count > 0 ? String(count) : '';
    });
  }

  /* ---------------------------------------------------------------- strings */

  const defaultStrings = {
    add: 'Add to basket',
    adding: 'Adding…',
    added: 'Added to basket',
    remove: 'Remove',
    empty: '',
    checking: 'Checking stock…',
    in_stock: 'In stock',
    out_of_stock: 'Out of stock · we keep it saved',
    unavailable: 'No longer available',
    cheaper: 'cheaper since you saved it',
    add_all: 'Add all to the basket'
  };

  let strings = defaultStrings;

  function readStrings() {
    const node = document.querySelector('[data-wishlist-strings]');
    if (!node) return;
    try {
      strings = Object.assign({}, defaultStrings, JSON.parse(node.textContent));
    } catch (error) {
      strings = defaultStrings;
    }
  }

  function escapeHtml(value) {
    return String(value == null ? '' : value).replace(/[&<>"']/g, (char) => ({
      '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;'
    }[char]));
  }

  /* ----------------------------------------------------------------- money */

  // The shop's money_format wraps the amount in markup (<span class=hidePrice>…</span>).
  // These tiles print the amount as text, so strip tags and collapse the leftover spaces.
  const moneyFormat = String(config.moneyFormat || '${{amount}}')
    .replace(/<[^>]*>/g, '')
    .replace(/\s+/g, ' ')
    .trim();

  function formatMoney(cents) {
    if (!Number.isFinite(cents)) return '';
    const format = moneyFormat;
    const value = cents / 100;

    function withDelimiters(precision, thousands, decimal) {
      const fixed = Math.abs(value).toFixed(precision);
      const parts = fixed.split('.');
      const whole = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, thousands);
      const fraction = parts[1] ? decimal + parts[1] : '';
      return (value < 0 ? '-' : '') + whole + fraction;
    }

    return format.replace(/\{\{\s*(\w+)\s*\}\}/g, (match, name) => {
      switch (name) {
        case 'amount': return withDelimiters(2, ',', '.');
        case 'amount_no_decimals': return withDelimiters(0, ',', '.');
        case 'amount_with_comma_separator': return withDelimiters(2, '.', ',');
        case 'amount_no_decimals_with_comma_separator': return withDelimiters(0, '.', ',');
        case 'amount_with_apostrophe_separator': return withDelimiters(2, "'", '.');
        case 'amount_no_decimals_with_space_separator': return withDelimiters(0, ' ', ',');
        case 'amount_with_space_separator': return withDelimiters(2, ' ', ',');
        case 'amount_with_period_and_space_separator': return withDelimiters(2, ' ', '.');
        default: return match;
      }
    });
  }

  /* ---------------------------------------------------- live price / stock */

  const liveCache = {};

  function productJsonUrl(item) {
    const root = String(config.routesRoot || (window.Shopify && Shopify.routes && Shopify.routes.root) || '/').replace(/\/$/, '');
    if (item.handle) return `${root}/products/${encodeURIComponent(item.handle)}.js`;
    if (item.url) return `${item.url.split('?')[0].replace(/\/$/, '')}.js`;
    return null;
  }

  function fetchLive(item) {
    const key = idOf(item);
    if (liveCache[key]) return Promise.resolve(liveCache[key]);
    const url = productJsonUrl(item);
    if (!url) return Promise.resolve(null);

    return fetch(url, { headers: { Accept: 'application/json' } })
      .then((response) => (response.ok ? response.json() : null))
      .then((product) => {
        if (!product) return null;
        const variants = product.variants || [];
        const variant = variants.find((entry) => String(entry.id) === String(item.variantId))
          || variants.find((entry) => entry.available)
          || variants[0]
          || null;
        const info = {
          found: true,
          available: variant ? !!variant.available : !!product.available,
          price: variant ? variant.price : product.price,
          variantId: variant ? String(variant.id) : item.variantId,
          title: product.title || item.title,
          type: product.type || item.type,
          url: product.url || item.url,
          image: (product.featured_image || item.image || '')
        };
        liveCache[key] = info;
        return info;
      })
      .catch(() => null);
  }

  /* ---------------------------------------------------------------- render */

  function tileHtml(item, index) {
    const url = item.url || (item.handle ? `/products/${item.handle}` : '#');
    const image = item.image
      ? `<img class="ap-tile__img" src="${escapeHtml(item.image)}" alt="${escapeHtml(item.title)}" width="600" height="600" loading="lazy">`
      : '';
    const savedPrice = item.priceCents ? formatMoney(item.priceCents) : (item.price || '');

    return `
      <article class="ap-tile is-loading" data-wish-id="${escapeHtml(item.variantId)}" style="--ap-tile-index:${index}">
        <a class="ap-tile__media" href="${escapeHtml(url)}" tabindex="-1" aria-hidden="true">
          ${image}
          <span class="ap-tile__shimmer" aria-hidden="true"></span>
          <span class="ap-tile__badge" data-tile-badge hidden></span>
          <span class="ap-tile__price" data-tile-price>${escapeHtml(savedPrice)}</span>
        </a>
        <div class="ap-tile__body">
          <span class="ap-tile__cat">${item.type ? escapeHtml(item.type) : ''}</span>
          <a class="ap-tile__name" href="${escapeHtml(url)}">${escapeHtml(item.title)}</a>
          <span class="ap-tile__status" data-tile-status>
            <span class="ap-tile__dot" aria-hidden="true"></span>${escapeHtml(strings.checking)}
          </span>
          <div class="ap-tile__actions">
            <button type="button" class="ap-tile__add" data-wish-add="${escapeHtml(item.variantId)}" disabled>${escapeHtml(strings.add)}</button>
            <button type="button" class="ap-tile__remove" data-wish-remove="${escapeHtml(item.variantId)}">${escapeHtml(strings.remove)}</button>
          </div>
        </div>
      </article>
    `;
  }

  function applyLive(item, info) {
    const tile = document.querySelector(`[data-wish-id="${CSS.escape(String(item.variantId))}"]`);
    if (!tile) return null;

    tile.classList.remove('is-loading');
    const status = tile.querySelector('[data-tile-status]');
    const priceEl = tile.querySelector('[data-tile-price]');
    const badge = tile.querySelector('[data-tile-badge]');
    const addBtn = tile.querySelector('[data-wish-add]');

    if (!info) {
      // Live lookup failed (offline, or the product is gone) — stay honest.
      tile.classList.add('is-unknown');
      if (status) status.textContent = strings.unavailable;
      if (addBtn) {
        addBtn.disabled = true;
        addBtn.classList.add('is-placeholder');
      }
      return null;
    }

    if (priceEl) priceEl.textContent = formatMoney(info.price);
    if (addBtn) {
      addBtn.dataset.wishAdd = info.variantId;
      addBtn.disabled = !info.available;
      addBtn.classList.toggle('is-placeholder', !info.available);
    }

    let drop = 0;
    if (item.priceCents && info.price < item.priceCents) drop = item.priceCents - info.price;

    if (info.available) {
      tile.classList.add('is-available');
      if (status) {
        status.innerHTML = drop
          ? `<span class="ap-tile__dot ap-tile__dot--gold" aria-hidden="true"></span>${escapeHtml(`${formatMoney(drop)} ${strings.cheaper}`)}`
          : `<span class="ap-tile__dot ap-tile__dot--ok" aria-hidden="true"></span>${escapeHtml(strings.in_stock)}`;
      }
      if (status && drop) status.classList.add('is-gold');
    } else {
      tile.classList.add('is-waiting');
      if (status) {
        status.innerHTML = `<span class="ap-tile__dot ap-tile__dot--wait" aria-hidden="true"></span>${escapeHtml(strings.out_of_stock)}`;
      }
      if (addBtn) {
        addBtn.disabled = true;
        addBtn.classList.add('is-placeholder');
      }
    }

    if (badge && drop && item.priceCents) {
      badge.textContent = `−${Math.round((drop / item.priceCents) * 100)}%`;
      badge.removeAttribute('hidden');
    }

    return { available: info.available, price: info.price, drop, variantId: info.variantId, title: info.title || item.title };
  }

  function refreshLive() {
    if (!list.length) return;
    const snapshot = list.slice();

    Promise.all(snapshot.map((item) => fetchLive(item).then((info) => applyLive(item, info))))
      .then((results) => {
        const rows = results.filter(Boolean);
        const inStock = rows.filter((row) => row.available);
        const total = inStock.reduce((sum, row) => sum + row.price, 0);
        const addAll = document.querySelector('[data-wishlist-add-all]');
        if (addAll) {
          if (inStock.length > 1) {
            addAll.textContent = `${strings.add_all} · ${formatMoney(total)}`;
            addAll.dataset.variantIds = inStock.map((row) => row.variantId).join(',');
            addAll.removeAttribute('hidden');
          } else {
            addAll.setAttribute('hidden', '');
          }
        }
      });
  }

  function renderGrid() {
    const grid = document.querySelector('[data-wishlist-grid]');
    if (!grid) return;
    readStrings();

    const shelf = document.querySelector('[data-wishlist-shelf]');
    const addAll = document.querySelector('[data-wishlist-add-all]');

    if (!list.length) {
      grid.innerHTML = `<p class="ap-card ap-empty">${escapeHtml(strings.empty)}</p>`;
      if (shelf) shelf.classList.add('is-empty');
      if (addAll) addAll.setAttribute('hidden', '');
      return;
    }

    if (shelf) shelf.classList.remove('is-empty');
    grid.innerHTML = list.map(tileHtml).join('');
    refreshLive();
  }

  function cartSections() {
    let sections = 'cart-icon-bubble';
    const cartDrawer = document.querySelector('cart-drawer');
    const drawerSection = cartDrawer && cartDrawer.closest('.shopify-section');
    if (drawerSection) sections += `,${drawerSection.id.replace('shopify-section-', '')}`;
    return sections;
  }

  function refreshCart(data) {
    const cartIconBubble = document.getElementById('cart-icon-bubble');
    if (cartIconBubble && data.sections && data.sections['cart-icon-bubble']) {
      cartIconBubble.innerHTML = data.sections['cart-icon-bubble'];
    }
    const cartDrawer = document.querySelector('cart-drawer');
    if (cartDrawer && typeof cartDrawer.renderContents === 'function') {
      const openDrawer = window.theme && theme.settings && (
        theme.settings.afterAtc === 'drawer'
        || (theme.settings.afterAtc === 'drawer_desktop' && theme.mediaMatches && theme.mediaMatches.md)
      );
      cartDrawer.renderContents(data, openDrawer);
    } else {
      document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:refresh', { bubbles: true }));
    }
    const addedId = data.variant_id || (data.items && data.items[0] && data.items[0].variant_id);
    document.dispatchEvent(new CustomEvent('on:cart:add', {
      bubbles: true,
      detail: { variantId: addedId }
    }));
  }

  function addToCart(variantIds, btn) {
    const ids = (Array.isArray(variantIds) ? variantIds : [variantIds]).filter(Boolean);
    if (!ids.length || (btn && btn.disabled)) return;
    const cartAddUrl = (window.theme && theme.routes && theme.routes.cartAdd) || '/cart/add.js';
    const label = btn ? btn.textContent : '';

    if (btn) {
      btn.disabled = true;
      btn.textContent = strings.adding;
    }

    fetch(cartAddUrl, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({
        items: ids.map((id) => ({ id, quantity: 1 })),
        sections: cartSections(),
        sections_url: window.location.pathname
      })
    })
      .then((response) => response.json().then((data) => {
        if (!response.ok || data.status) throw new Error(data.description || data.message || 'Add to cart failed');
        return data;
      }))
      .then((data) => {
        refreshCart(data);
        removeMany(ids, true);
        if (btn && btn.isConnected && btn.matches('[data-wishlist-add-all]')) {
          btn.textContent = strings.added;
          btn.classList.add('is-done');
          setTimeout(() => {
            if (btn.isConnected) {
              btn.textContent = label;
              btn.classList.remove('is-done');
            }
          }, 2200);
        }
      })
      .catch((error) => {
        console.error(error); // eslint-disable-line no-console
        if (btn) {
          btn.textContent = label;
          btn.disabled = false;
        }
      });
  }

  function onToggleClick(evt) {
    const btn = evt.target.closest('[data-wishlist-toggle]');
    if (!btn) return;
    evt.preventDefault();
    evt.stopPropagation();
    toggle({
      productId: btn.dataset.productId,
      variantId: btn.dataset.variantId,
      handle: btn.dataset.handle,
      title: btn.dataset.title,
      url: btn.dataset.url,
      price: btn.dataset.price,
      priceCents: btn.dataset.priceCents,
      type: btn.dataset.type,
      image: btn.dataset.image
    });
    syncButton(btn);
  }

  document.addEventListener('click', (evt) => {
    onToggleClick(evt);
    const addBtn = evt.target.closest('[data-wish-add]');
    if (addBtn) {
      evt.preventDefault();
      addToCart(addBtn.getAttribute('data-wish-add'), addBtn);
    }
    const addAllBtn = evt.target.closest('[data-wishlist-add-all]');
    if (addAllBtn) {
      evt.preventDefault();
      addToCart((addAllBtn.dataset.variantIds || '').split(',').filter(Boolean), addAllBtn);
    }
    const removeBtn = evt.target.closest('[data-wish-remove]');
    if (removeBtn) {
      evt.preventDefault();
      const id = removeBtn.getAttribute('data-wish-remove');
      const tile = removeBtn.closest('.ap-tile');
      if (tile && !window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
        tile.classList.add('is-leaving');
        setTimeout(() => remove(id), 220);
      } else {
        remove(id);
      }
    }
  });

  document.addEventListener('on:wishlist:change', () => {
    syncButtons();
    syncCounts();
    renderGrid();
  });

  function boot() {
    persistLocal(list);
    persistMetafield(list);
    syncButtons();
    syncCounts();
    renderGrid();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  window.dimarkWishlist = { read, has, toggle, remove, removeMany };
})();
